import { useQuery } from '@graphcommerce/graphql'
import { AccountAddressFragment } from '@graphcommerce/magento-customer/components/AccountAddress/AccountAddress.gql'
import { ApolloCustomerErrorAlert } from '@graphcommerce/magento-customer/components/ApolloCustomerError/ApolloCustomerErrorAlert'
import { CountryRegionsDocument } from '@graphcommerce/magento-store'
import { useFormGqlMutation } from '@graphcommerce/react-hook-form'
import { i18n } from '@lingui/core'
import { Button } from '@mui/material'
import { GraphQLError } from 'graphql'
import { useGoogleAddressValidation } from '../../../hooks/useGoogleAddressValidation'
import { StandardButton } from '../../Button/ButtonStyles'
import { AccountAddressFreightFragment } from '../AccountAddresses/AccountAddressFreight.gql'
import { AddressFields } from '../AddressFields/AddressFields'
import { NameFields } from '../NameFields/NameFields'
import { TelephoneFields } from '../TelephoneFields/TelephoneFields'
import { UpdateCustomerAddressDocument } from './UpdateCustomerAddress.gql'

type EditAddressFormProps = {
  addressType?: 'billing' | 'shipping'
  address?: AccountAddressFragment & AccountAddressFreightFragment
  onComplete?: () => void
  onError?: (errors: readonly GraphQLError[] | undefined) => void
  onSuccess?: () => void
  onCancel?: () => void
  renderInsideForm?: boolean
}

export function EditAddressForm(props: EditAddressFormProps) {
  const countryQuery = useQuery(CountryRegionsDocument, { fetchPolicy: 'cache-and-network' })
  const countries = countryQuery.data?.countries ?? countryQuery.previousData?.countries
  const {
    address,
    onComplete,
    onError,
    onSuccess,
    onCancel,
    renderInsideForm = true,
    addressType = 'shipping',
  } = props

  const { validateCustomerAddress, processFormBasedOnResult } = useGoogleAddressValidation()

  const form = useFormGqlMutation(
    UpdateCustomerAddressDocument,
    {
      defaultValues: {
        id: address?.id ?? undefined,
        firstname: address?.firstname,
        lastname: address?.lastname,
        company: address?.company,
        street: address?.street?.[0] ?? undefined,
        postcode: address?.postcode,
        city: address?.city,
        countryCode: address?.country_code || 'US',
        telephone: address?.telephone,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        region: address?.region?.region_id,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        houseNumber: address?.street?.[1],
        // Freight fields
        is_freight_address: address?.is_freight_address,
        appointment_required: address?.appointment_required,
        loading_dock: address?.loading_dock,
        delivery_to_door: address?.delivery_to_door,
        limited_access_delivery: address?.limited_access_delivery,
        residential_delivery: address?.residential_delivery,
        days_hours_delivery: address?.days_hours_delivery
          ? address.days_hours_delivery
              ?.filter((a) => !!a)
              .map((a) => ({
                label: a?.label,
                available: a?.available,
                from: a?.from,
                to: a?.to,
              }))
          : ['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'].map((label) => ({
              label,
              available: !['Sat', 'Sun'].includes(label),
              from: '08:00am',
              to: '05:00pm',
            })),
      },
      onBeforeSubmit: async (formData) => {
        const region = countries
          ?.find((country) => country?.two_letter_abbreviation === formData.countryCode)
          ?.available_regions?.find((r) => r?.id === formData.region)
        const regionData = {
          region:
            (region && {
              region: region.name,
              region_code: region.code,
              region_id: region.id,
            }) ??
            null,
        }

        const googleValidResult = await validateCustomerAddress({
          ...formData,
          regionCode: region ? region.code : '',
        })
        const { isValid, formDataProcessed } = processFormBasedOnResult(
          googleValidResult,
          form,
          formData,
        )

        if (!isValid) {
          return false
        }

        return {
          ...formDataProcessed,
          ...regionData,
        }
      },
      onComplete: ({ errors }) => {
        if (errors?.length) {
          if (onError) onError(errors)
        } else if (onSuccess) onSuccess()
        if (onComplete) onComplete()
      },
    },
    { errorPolicy: 'all' },
  )

  const { handleSubmit, formState, required, error, register, valid } = form
  const submitHandler = handleSubmit(() => {})

  const formContent = (
    <>
      <AddressFields
        addressType={addressType}
        form={form}
        regionFieldName='region'
        isFreight={address?.is_freight_address || false}
        telephoneField={
          <TelephoneFields
            formState={formState}
            required={required.telephone}
            register={register}
          />
        }
        nameFields={<NameFields form={form} prefix className='max-w-[260px]' />}
      />
      <div className='mb-[10px] mt-5 flex flex-col justify-start gap-[20px] pb-0 md:flex-row'>
        <StandardButton
          type='button'
          variant='contained'
          size='small'
          loading={formState.isSubmitting}
          className='add-to-cart w-auto bg-material-ui-blue uppercase '
          onClick={submitHandler}
        >
          {i18n._('Save')}
        </StandardButton>
        {onCancel && (
          <Button
            color='primary'
            className='w-full bg-pure-white uppercase md:w-auto'
            variant='outlined'
            onClick={onCancel}
            size='small'
          >
            {i18n._('Cancel')}
          </Button>
        )}
      </div>
      <ApolloCustomerErrorAlert error={error} />
    </>
  )

  if (!renderInsideForm) {
    return formContent
  }

  return (
    <form
      onSubmit={submitHandler}
      noValidate
      className='mb-0 flex flex-col gap-y-2 pb-0'
      autoComplete='on'
    >
      {formContent}
    </form>
  )
}
